import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Globe2, Sparkles, Lock } from 'lucide-react';
import useSEO from '@/hooks/useSEO';
import { useAuth } from '@/contexts/AuthContext';

/**
 * /countries — Browse the country catalogue.
 *
 * Free tier: visit any country, read random facts, take ONE quiz per day.
 * Paid tier: unlimited quizzes; quizzes about countries linked to a language
 * the family is learning are surfaced more prominently on the language hub.
 */
export default function Countries() {
    useSEO({
        title: 'World facts & country quizzes — Linguistic Quest',
        description: 'Discover curated facts about the countries behind the languages you learn. Plus daily country quizzes that reward points on the family leaderboard.',
        path: '/countries',
    });
    const { user } = useAuth();
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/countries')
            .then(({ data }) => setCountries(data.countries || []))
            .finally(() => setLoading(false));
    }, []);

    const live = countries.filter((c) => c.status !== 'coming_soon');
    const coming = countries.filter((c) => c.status === 'coming_soon');

    return (
        <main className="max-w-7xl mx-auto px-6 py-14" data-testid="page-countries">
            <p className="text-sm font-semibold uppercase tracking-wider text-lq-purple-500 inline-flex items-center gap-1"><Globe2 size={14}/> Discover</p>
            <h1 className="mt-1 font-display text-4xl md:text-5xl font-semibold text-lq-ink-black">Country facts &amp; quizzes</h1>
            <p className="mt-4 max-w-2xl text-lq-ink-700 leading-relaxed">
                Each language we teach belongs to a place — a market, a coastline, a kitchen. Browse the countries
                behind your family's learning journey, read curated facts (free for everyone), and challenge yourselves
                with a daily quiz. Quizzes are paid-tier; on the free plan, every parent gets <strong>one question a day</strong>.
            </p>

            {loading && <p className="mt-8 text-lq-ink-500">Loading…</p>}

            {!loading && live.length > 0 && (
                <section className="mt-10">
                    <h2 className="font-display text-2xl font-semibold text-lq-ink-900 inline-flex items-center gap-2"><Sparkles size={18}/> Available now</h2>
                    <div className="mt-5 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="countries-live-grid">
                        {live.map((c) => <CountryCard key={c.slug} c={c} user={user} />)}
                    </div>
                </section>
            )}

            {!loading && coming.length > 0 && (
                <section className="mt-12">
                    <h2 className="font-display text-xl font-semibold text-lq-ink-900">Coming soon</h2>
                    <p className="mt-1 text-sm text-lq-ink-500">We add new countries as our editorial team finishes their fact files.</p>
                    <div className="mt-5 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="countries-coming-grid">
                        {coming.map((c) => (
                            <div key={c.slug} className="bg-lq-paper border border-lq-ink-100 rounded-xl p-4 text-center opacity-70">
                                <div className="text-3xl">{c.flag_emoji}</div>
                                <div className="mt-1 text-sm font-medium text-lq-ink-700">{c.name}</div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </main>
    );
}

function CountryCard({ c, user }) {
    const isPaid = user && ['trialing', 'active'].includes((user.subscription_status || '').toLowerCase()) && ['explorer', 'polyglot', 'global'].includes((user.subscription_tier || '').toLowerCase());
    const quizLive = (c.quiz_status || (c.quiz_question_count > 0 ? 'live' : 'coming_soon')) === 'live';
    return (
        <Link
            to={`/countries/${c.slug}`}
            className="group bg-white border border-lq-ink-100 rounded-2xl p-5 hover:border-lq-purple-300 hover:shadow transition-all flex flex-col"
            data-testid={`country-card-${c.slug}`}
        >
            <div className="flex items-start justify-between">
                <span className="text-5xl" aria-hidden="true">{c.flag_emoji}</span>
                {!quizLive && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-lq-peach-100 text-lq-peach-600">Quiz coming soon</span>
                )}
                {quizLive && !isPaid && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-lq-purple-100 text-lq-purple-700 inline-flex items-center gap-1"><Lock size={10}/> Quiz on paid</span>
                )}
            </div>
            <h3 className="mt-3 font-display text-lg font-semibold text-lq-ink-900">{c.name}</h3>
            {c.region && <p className="text-xs text-lq-ink-500">{c.region}</p>}
            {c.blurb && <p className="mt-2 text-sm text-lq-ink-700 line-clamp-3">{c.blurb}</p>}
            <div className="mt-auto pt-4 flex items-center justify-between text-xs text-lq-ink-500">
                <span>{c.fact_count || 0} facts · {quizLive ? `${c.quiz_question_count || 0} quiz Qs` : 'quiz soon'}</span>
                {(c.language_slugs || []).length > 0 && (
                    <span className="capitalize">{(c.language_slugs || [])[0]}</span>
                )}
            </div>
        </Link>
    );
}
